import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { ArrowUpRight, Leaf, Timer, Sparkles, MapPin } from "lucide-react";

const TOWNS = ["Dillsburg", "Wellsville", "Etters", "Camp Hill", "Carlisle", "York", "Dover"];

const HERO = "https://images.pexels.com/photos/15801079/pexels-photo-15801079.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const LATTE = "https://images.unsplash.com/photo-1670404161009-29548c027d06?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxhcnRpc2FuYWwlMjBsYXR0ZSUyMGFydHxlbnwwfHx8fDE3ODc0MTE5MTV8MA&ixlib=rb-4.1.0&q=85";
const COLD = "https://images.unsplash.com/photo-1592663527359-cf6642f54cff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxjb2xkJTIwYnJldyUyMGNvZmZlZXxlbnwwfHx8fDE3ODc0MTE5MTV8MA&ixlib=rb-4.1.0&q=85";
const BARISTA = "https://images.unsplash.com/photo-1514066558159-fc8c737ef259?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxiYXJpc3RhJTIwbWFraW5nJTIwY29mZmVlfGVufDB8fHx8MTc4NzQxMTkxNXww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  return (
    <div data-testid="home-page" className="min-h-screen grain-overlay">
      <SiteHeader />

      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-14 md:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <p data-testid="hero-eyebrow" className="label-eyebrow mb-6">
                Hand-crafted · Small batch · Dillsburg, PA
              </p>
              <h1
                data-testid="hero-title"
                className="font-serif-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-[#1A1514]"
              >
                Custom coffees,
                <br />
                <span className="italic text-[#C05A46]">brewed by hand.</span>
              </h1>
              <p
                data-testid="hero-subtitle"
                className="mt-6 text-base md:text-lg text-[#4A4341] max-w-xl leading-relaxed"
              >
                Build your drink the way you like it — pick a base, choose your milk,
                add syrups & extras. I&rsquo;ll deliver it to your door at the time
                you choose. No app downloads, no fuss.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/order"
                  data-testid="hero-start-order-btn"
                  className="btn-brand inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold tracking-wide"
                >
                  Start your order
                  <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
                </Link>
                <Link
                  to="/signin"
                  data-testid="hero-signin-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-[#C05A46]/40 px-7 py-3.5 text-sm font-semibold tracking-wide text-[#C05A46] hover:bg-[#C05A46] hover:text-white transition-colors"
                >
                  Sign in for reorder
                </Link>
                <Link
                  to="/track"
                  data-testid="hero-track-order-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-black/15 px-7 py-3.5 text-sm font-semibold tracking-wide text-[#1A1514] hover:border-[#1A1514] transition-colors"
                >
                  Track by number
                </Link>
              </div>
              <p className="mt-3 text-xs text-[#7B7573]" data-testid="hero-guest-hint">
                No account needed — you can also order as a guest.
              </p>

              <dl className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
                <div>
                  <dt className="label-eyebrow">Made in</dt>
                  <dd className="mt-1 font-serif-display text-2xl">~7 min</dd>
                </div>
                <div>
                  <dt className="label-eyebrow">Delivered at</dt>
                  <dd className="mt-1 font-serif-display text-2xl">Your time</dd>
                </div>
                <div>
                  <dt className="label-eyebrow">Pay on</dt>
                  <dd className="mt-1 font-serif-display text-2xl">Delivery</dd>
                </div>
              </dl>
            </div>

            <div className="lg:col-span-5 order-1 lg:order-2">
              <div className="relative">
                <div className="absolute -top-4 -left-4 hidden lg:block h-28 w-28 rounded-full bg-[#D69F4C]/70" />
                <img
                  data-testid="hero-image"
                  src={HERO}
                  alt="Barista pouring latte art"
                  className="relative rounded-2xl w-full h-[380px] md:h-[500px] object-cover shadow-[0_30px_60px_-30px_rgba(26,21,20,0.35)]"
                />
                <div className="absolute -bottom-6 -right-4 hidden md:block bg-white border border-black/10 rounded-xl p-4 shadow-lg">
                  <p className="label-eyebrow text-[#C05A46]">Today</p>
                  <p className="mt-1 font-serif-display text-2xl">Now open</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-black/10 bg-[#EAEAE3]/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="flex items-end justify-between gap-8 mb-12">
            <div>
              <p className="label-eyebrow">The process</p>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl tracking-tight">
                Three sips and it&rsquo;s in your hand.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Sparkles, title: "01 · Build it", body: "Pick a base drink, then customize milk, syrups & extras to taste." },
              { icon: Timer, title: "02 · Choose delivery window", body: "Tell me when you'd like it delivered. I start brewing right before I head out." },
              { icon: Leaf, title: "03 · Enjoy fresh", body: "Pay on delivery with cash or e-transfer. Small batch, big flavor." },
            ].map((s, i) => (
              <article
                key={i}
                data-testid={`how-step-${i + 1}`}
                className="selectable-card p-8"
              >
                <s.icon className="h-6 w-6 text-[#C05A46]" strokeWidth={1.5} />
                <h3 className="mt-6 font-serif-display text-2xl tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm text-[#4A4341] leading-relaxed">{s.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* MENU PREVIEW */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="relative rounded-2xl overflow-hidden h-80 md:h-96 group">
            <img src={LATTE} alt="Latte" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-[#F5F5F0]">
              <p className="label-eyebrow text-white/80">Hot drinks</p>
              <h3 className="mt-2 font-serif-display text-3xl tracking-tight text-white">Lattes, cappuccinos, americanos</h3>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden h-80 md:h-96 group">
            <img src={COLD} alt="Iced coffee" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-[#F5F5F0]">
              <p className="label-eyebrow text-white/80">Cold drinks</p>
              <h3 className="mt-2 font-serif-display text-3xl tracking-tight text-white">Iced coffees & cold brews</h3>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section data-testid="service-area-section" className="border-t border-black/10 bg-[#EAEAE3]/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="label-eyebrow">Where I serve</p>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl tracking-tight">
              Local coffee near Dillsburg, Wellsville & beyond.
            </h2>
            <p className="mt-4 text-sm md:text-base text-[#4A4341] leading-relaxed max-w-md">
              The Cozy Bean is a small, home-grown café business in York and Cumberland
              County, PA. Looking for coffee near you? Hand-crafted lattes, americanos and
              iced coffees are available for delivery or pickup across these towns.
            </p>
          </div>
          <ul className="lg:col-span-7 flex flex-wrap content-start gap-3" data-testid="service-area-list">
            {TOWNS.map((t) => (
              <li
                key={t}
                data-testid={`service-area-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-2 rounded-full border border-black/15 bg-white px-5 py-2.5 text-sm text-[#1A1514] transition-colors hover:border-[#C05A46] hover:text-[#C05A46]"
              >
                <MapPin className="h-4 w-4 text-[#C05A46]" strokeWidth={1.75} />
                Coffee near {t}, PA
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ABOUT / CTA */}
      <section className="border-t border-black/10 bg-[#1A1514] text-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="label-eyebrow text-[#D69F4C]">The story</p>
            <h2 className="mt-4 font-serif-display text-4xl md:text-5xl tracking-tight leading-tight">
              Every cup made by hand, for one person at a time.
            </h2>
            <p className="mt-6 text-[#EAEAE3]/80 leading-relaxed max-w-lg">
              The Cozy Bean is a one-person operation. That means every latte, americano
              and iced coffee is pulled, poured and finished for you, in the moment.
            </p>
            <Link
              to="/order"
              data-testid="cta-order-btn"
              className="mt-10 btn-brand inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
            >
              Order a coffee <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <img
            src={BARISTA}
            alt="Barista at work"
            className="rounded-2xl w-full h-80 md:h-[440px] object-cover"
          />
        </div>
      </section>

      <footer className="border-t border-black/10 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <p className="label-eyebrow text-[#C05A46]">Contact</p>
            <h3 className="mt-3 font-serif-display text-2xl tracking-tight">Say hi anytime.</h3>
            <p className="mt-3 text-sm text-[#4A4341] leading-relaxed max-w-xs">
              Questions, special requests, or corporate orders — reach out directly and I&rsquo;ll get back to you.
            </p>
          </div>
          <div className="text-sm text-[#1A1514] space-y-3">
            <p className="label-eyebrow text-[#7B7573]">Direct line</p>
            <p data-testid="footer-phone" className="font-mono-jet text-lg">
              <a href="tel:+17175509331" className="hover:text-[#C05A46] transition-colors">717.550.9331</a>
            </p>
            <p data-testid="footer-email" className="font-mono-jet">
              <a href="mailto:lrarnold0809@gmail.com" className="hover:text-[#C05A46] transition-colors">
                lrarnold0809@gmail.com
              </a>
            </p>
          </div>
          <div className="text-sm text-[#1A1514]">
            <p className="label-eyebrow text-[#7B7573]">Based in</p>
            <p data-testid="footer-location" className="mt-2 font-serif-display text-xl leading-snug">
              Dillsburg,<br />Pennsylvania
            </p>
            <p className="mt-3 text-xs text-[#7B7573]">Delivering across the area Monday through Friday.</p>
          </div>
        </div>
        <div className="border-t border-black/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center justify-between gap-4 text-xs text-[#7B7573]">
            <p>© {new Date().getFullYear()} The Cozy Bean · Hand-brewed in Dillsburg, PA</p>
            <Link to="/admin" data-testid="footer-admin-link" className="hover:text-[#1A1514] transition-colors">
              Owner sign in
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
